import type { ColDef } from "ag-grid-community";
import { Typography } from "antd";
import React, { useMemo } from "react";
import Grid from "../../../../atoms/Grid";
import { SectionLoader } from "../../../../atoms/SectionLoader/SectionLoader";
import { formatCurrency, formatDateByType } from "../../../../libs/utils";
import { Invoice } from "../types";
import { useGetReceiptInvoices } from "./api/useGetReceiptInvoices";
import { PendingInvoiceActionCell } from "./PendingInvoiceActionCell";
import { convertToNormalCase } from "./utils";

const GRID_HEADER_PX = 42;
const GRID_ROW_PX = 42;
const GRID_MAX_VISIBLE_ROWS = 8;
const GRID_MIN_HEIGHT_PX = GRID_HEADER_PX + GRID_ROW_PX * 4;

const defaultSettings = {
  sortable: true,
  resizable: true,
  minWidth: 100,
};

const centeredContainerStyle: React.CSSProperties = {
  minHeight: GRID_MIN_HEIGHT_PX,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 12px",
  textAlign: "center",
};

type PendingInvoicesGridProps = {
  leaseId?: string;
  tenantId?: string;
  isEnabled?: boolean;
  disabled?: boolean;
  addedInvoiceIds?: string[];
  onAddInvoice: (invoice: Invoice) => void;
};

export const PendingInvoicesGrid: React.FC<PendingInvoicesGridProps> = ({
  leaseId,
  tenantId,
  isEnabled = false,
  disabled,
  addedInvoiceIds = [],
  onAddInvoice,
}) => {
  const { invoices, isLoadingInvoices } = useGetReceiptInvoices({
    leaseId,
    tenantId,
    isEnabled,
  });

  const gridHeight = useMemo(() => {
    const rowCount = Math.max(invoices.length, 1);
    const visibleRows = Math.min(rowCount, GRID_MAX_VISIBLE_ROWS);
    return Math.max(GRID_HEADER_PX + visibleRows * GRID_ROW_PX, GRID_MIN_HEIGHT_PX);
  }, [invoices.length]);

  const actionCellParams = useMemo(
    () => ({
      addedInvoiceIds,
      disabled,
      onAddInvoice,
    }),
    [addedInvoiceIds, disabled, onAddInvoice],
  );

  const columns = useMemo<ColDef<Invoice>[]>(
    () => [
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        width: 120,
        valueFormatter: ({ value }) => value || "--",
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.2,
        minWidth: 120,
        valueFormatter: ({ value }) => convertToNormalCase(value) || "--",
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        width: 130,
        valueFormatter: ({ value }) => formatDateByType(value),
      },
      {
        field: "totalAmount",
        headerName: "Amount",
        width: 170,
        type: "rightAligned",
        valueFormatter: ({ value }) => formatCurrency(value),
      },
      {
        colId: "balance",
        headerName: "Balance",
        width: 170,
        type: "rightAligned",
        valueFormatter: ({ data }) => formatCurrency(data?.balance ?? data?.totalAmount ?? 0),
      },
      {
        colId: "action",
        headerName: "",
        width: 48,
        maxWidth: 48,
        minWidth: 48,
        sortable: false,
        resizable: false,
        suppressMenu: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        cellRenderer: PendingInvoiceActionCell,
        cellRendererParams: actionCellParams,
      },
    ],
    [actionCellParams],
  );

  if (!isLoadingInvoices && !invoices.length)
    return (
      <div style={centeredContainerStyle}>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          No pending invoices found.
        </Typography.Paragraph>
      </div>
    );

  return (
    <SectionLoader isLoading={isLoadingInvoices} containerStyle={{ minHeight: GRID_MIN_HEIGHT_PX }}>
      <Grid
        className="pl-0 pr-0"
        columns={columns}
        data={invoices}
        defaultSettings={defaultSettings}
        pagination={false}
        height={gridHeight}
        headerHeight={GRID_HEADER_PX}
        rowHeight={GRID_ROW_PX}
        refreshAction={addedInvoiceIds.join(",")}
      />
    </SectionLoader>
  );
};
